import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Pause,
  Play,
  Settings2,
} from "lucide-react";
import { apiRequest } from "../lib/queryClient";

interface MetaEntityInfo {
  entityType: "campaign" | "adset";
  entityId: string;
  entityName: string;
  status: string;
  dailyBudget: number | null;
  lifetimeBudget: number | null;
  spend: string;
}

interface ManualEntityControlsProps {
  entities: MetaEntityInfo[];
  forUserId?: string;
}

function EntityRow({ entity, forUserId }: { entity: MetaEntityInfo; forUserId?: string }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customBudget, setCustomBudget] = useState("");

  const currentBudget = entity.dailyBudget ?? entity.lifetimeBudget;
  const isLifetime = entity.dailyBudget == null && entity.lifetimeBudget != null;
  const isActive = entity.status === "ACTIVE";

  const executeAction = async (actionPayload: Record<string, unknown>) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const resp = await apiRequest<{ success?: boolean; message?: string }>(
        "POST",
        "/api/marketing-analyst/chat-action",
        {
          ...actionPayload,
          forUserId,
        }
      );
      setResult(resp.message || t("manualEntity.done"));
    } catch (err) {
      setError((err as Error).message || t("manualEntity.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleBudgetChange = (percent: number) => {
    if (!currentBudget) return;
    const newBudget = Math.round(currentBudget * (1 + percent / 100));
    const directionKey = percent > 0 ? "manualEntity.budgetUp" : "manualEntity.budgetDown";
    executeAction({
      type: "update_budget",
      entityType: entity.entityType,
      entityId: entity.entityId,
      entityName: entity.entityName,
      newDailyBudget: isLifetime ? undefined : newBudget,
      newLifetimeBudget: isLifetime ? newBudget : undefined,
      description: t(directionKey, {
        from: currentBudget,
        to: newBudget,
        percent: percent > 0 ? `+${percent}` : `${percent}`,
      }),
    });
  };

  const handleCustomBudget = () => {
    const value = parseFloat(customBudget);
    if (isNaN(value) || value <= 0) return;
    executeAction({
      type: "update_budget",
      entityType: entity.entityType,
      entityId: entity.entityId,
      entityName: entity.entityName,
      newDailyBudget: isLifetime ? undefined : value,
      newLifetimeBudget: isLifetime ? value : undefined,
      description: t("manualEntity.budgetSet", { value }),
    });
    setShowCustom(false);
    setCustomBudget("");
  };

  const handleToggleStatus = () => {
    const newStatus = isActive ? "PAUSED" : "ACTIVE";
    const labelKey = isActive ? "manualEntity.pause" : "manualEntity.start";
    const targetKey =
      entity.entityType === "campaign" ? "manualEntity.campaignAcc" : "manualEntity.adsetAcc";
    executeAction({
      type: "update_status",
      entityType: entity.entityType,
      entityId: entity.entityId,
      entityName: entity.entityName,
      newStatus,
      description: `${t(labelKey)} ${t(targetKey)}`,
    });
  };

  const typeLabel = t(
    entity.entityType === "campaign" ? "manualEntity.campaign" : "manualEntity.adset"
  );
  const statusColor = isActive ? "text-emerald-400" : "text-yellow-400";
  const statusLabel = isActive ? t("manualEntity.active") : entity.status;

  return (
    <div className="border border-white/[0.06] rounded-lg p-3 space-y-2 bg-white/[0.02]">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
              {typeLabel}
            </span>
            <span className={`text-[10px] ${statusColor}`}>{statusLabel}</span>
          </div>
          <div className="text-xs text-white/80 truncate" title={entity.entityName}>
            {entity.entityName}
          </div>
          {currentBudget != null && (
            <div className="text-[10px] text-white/40 mt-0.5">
              {t("manualEntity.budgetLabel")}: {currentBudget} {t("common.currency")}
              {isLifetime ? t("manualEntity.lifetimeSuffix") : t("manualEntity.dailySuffix")} |{" "}
              {t("manualEntity.spend")}: {entity.spend} {t("common.currency")}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-[11px] text-white/50 animate-pulse">{t("manualEntity.executing")}</div>
      ) : result ? (
        <div className="text-[11px] text-emerald-400">{result}</div>
      ) : error ? (
        <div className="text-[11px] text-red-400">{error}</div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {currentBudget != null && (
          <>
            <button
              onClick={() => handleBudgetChange(20)}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 text-[11px] font-medium transition-colors disabled:opacity-40"
              title={t("manualEntity.budgetUpTitle")}
            >
              <TrendingUp className="w-3 h-3" />
              +20%
            </button>
            <button
              onClick={() => handleBudgetChange(-20)}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 rounded bg-orange-600/20 hover:bg-orange-600/30 text-orange-300 text-[11px] font-medium transition-colors disabled:opacity-40"
              title={t("manualEntity.budgetDownTitle")}
            >
              <TrendingDown className="w-3 h-3" />
              -20%
            </button>
            <button
              onClick={() => setShowCustom(!showCustom)}
              disabled={loading}
              className="flex items-center gap-1 px-2 py-1 rounded bg-white/[0.06] hover:bg-white/[0.1] text-white/60 text-[11px] font-medium transition-colors disabled:opacity-40"
              title={t("manualEntity.customTitle")}
            >
              <Settings2 className="w-3 h-3" />
              {t("manualEntity.custom")}
            </button>
          </>
        )}
        <button
          onClick={handleToggleStatus}
          disabled={loading}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors disabled:opacity-40 ${
            isActive
              ? "bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-300"
              : "bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300"
          }`}
          title={isActive ? t("manualEntity.pause") : t("manualEntity.start")}
        >
          {isActive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {isActive ? t("manualEntity.pause") : t("manualEntity.start")}
        </button>
      </div>

      {showCustom && (
        <div className="flex items-center gap-2 mt-1">
          <input
            type="number"
            value={customBudget}
            onChange={(e) => setCustomBudget(e.target.value)}
            placeholder={t("manualEntity.newBudgetPlaceholder")}
            className="flex-1 bg-white/[0.06] border border-white/[0.1] rounded px-2 py-1 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
            min="1"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCustomBudget();
            }}
          />
          <button
            onClick={handleCustomBudget}
            disabled={!customBudget || parseFloat(customBudget) <= 0}
            className="px-2 py-1 rounded bg-blue-600/30 hover:bg-blue-600/40 text-blue-300 text-[11px] font-medium transition-colors disabled:opacity-40"
          >
            {t("manualEntity.apply")}
          </button>
        </div>
      )}
    </div>
  );
}

export function ManualEntityControls({ entities, forUserId }: ManualEntityControlsProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (!entities || entities.length === 0) return null;

  const campaigns = entities.filter((e) => e.entityType === "campaign");
  const adsets = entities.filter((e) => e.entityType === "adset");

  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-white/50 hover:text-white/70 transition-colors w-full"
      >
        <Settings2 className="w-3.5 h-3.5" />
        <span className="font-medium">{t("manualEntity.toggle", { count: entities.length })}</span>
        {expanded ? (
          <ChevronUp className="w-3.5 h-3.5 ml-auto" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 ml-auto" />
        )}
      </button>

      {expanded && (
        <div className="mt-2 space-y-3">
          {campaigns.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-white/30 font-semibold">
                {t("manualEntity.campaigns")}
              </div>
              {campaigns.map((e) => (
                <EntityRow key={e.entityId} entity={e} forUserId={forUserId} />
              ))}
            </div>
          )}
          {adsets.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-white/30 font-semibold">
                {t("manualEntity.adsets")}
              </div>
              {adsets.map((e) => (
                <EntityRow key={e.entityId} entity={e} forUserId={forUserId} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
